import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';

import { Agents } from '@air/api';
import { type ListAgentSessionsResponse } from '@air/server-sdk';
import { callAirApi } from '@air/utils-api';

import {
  buildPrivateWorkspaceAgentSessionsListArchivedParams,
  buildPrivateWorkspaceAgentSessionsListInfiniteParams,
  getPrivateWorkspaceAgentSessionsListInfiniteKey,
} from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionsListInfinite';
import { buildWorkspaceAgentSessionListCachePatch } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentSessionListCachePatch';
import {
  movePrivateWorkspaceAgentSessionToArchivedListCache,
  removePrivateWorkspaceAgentSessionFromListCache,
  upsertPrivateWorkspaceAgentSessionInListCache,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/patchPrivateWorkspaceAgentSessionsListCache';

export type UsePrivateWorkspaceAgentSessionArchiveMutationParams = NonNullable<
  Parameters<typeof Agents.Sessions.archive>[0]
>;

type ArchiveMutationContext = {
  liveListKey: ReturnType<typeof getPrivateWorkspaceAgentSessionsListInfiniteKey>;
  archivedListKey: ReturnType<typeof getPrivateWorkspaceAgentSessionsListInfiniteKey>;
  previousLiveList: InfiniteData<ListAgentSessionsResponse> | undefined;
  previousArchivedList: InfiniteData<ListAgentSessionsResponse> | undefined;
};

export const usePrivateWorkspaceAgentSessionArchiveMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UsePrivateWorkspaceAgentSessionArchiveMutationParams) =>
      callAirApi(() => Agents.Sessions.archive(params)),
    onMutate: async (variables) => {
      const liveListParams = buildPrivateWorkspaceAgentSessionsListInfiniteParams(variables.workspaceId);
      const archivedListParams = buildPrivateWorkspaceAgentSessionsListArchivedParams(variables.workspaceId);
      const liveListKey = getPrivateWorkspaceAgentSessionsListInfiniteKey(liveListParams);
      const archivedListKey = getPrivateWorkspaceAgentSessionsListInfiniteKey(archivedListParams);

      await Promise.all([
        queryClient.cancelQueries({ queryKey: liveListKey }),
        queryClient.cancelQueries({ queryKey: archivedListKey }),
      ]);

      const previousLiveList = queryClient.getQueryData<InfiniteData<ListAgentSessionsResponse>>(liveListKey);
      const previousArchivedList = queryClient.getQueryData<InfiniteData<ListAgentSessionsResponse>>(archivedListKey);

      movePrivateWorkspaceAgentSessionToArchivedListCache(
        queryClient,
        variables.workspaceId,
        variables.sessionId,
        buildWorkspaceAgentSessionListCachePatch(new Date().toISOString(), { status: 'archived' }),
      );

      return { liveListKey, archivedListKey, previousLiveList, previousArchivedList } satisfies ArchiveMutationContext;
    },
    onError: (_error, variables, context) => {
      if (!context) {
        return;
      }

      const archivedListParams = buildPrivateWorkspaceAgentSessionsListArchivedParams(variables.workspaceId);
      removePrivateWorkspaceAgentSessionFromListCache(queryClient, archivedListParams, variables.sessionId);

      if (context.previousLiveList) {
        queryClient.setQueryData(context.liveListKey, context.previousLiveList);
      } else {
        // Rollback path: invalidation failure would leave stale data until the next refetch.
        void queryClient.invalidateQueries({ queryKey: context.liveListKey });
      }

      queryClient.setQueryData(context.archivedListKey, context.previousArchivedList);
    },
    onSuccess: (archivedSession, variables) => {
      upsertPrivateWorkspaceAgentSessionInListCache(
        queryClient,
        buildPrivateWorkspaceAgentSessionsListArchivedParams(variables.workspaceId),
        archivedSession,
      );
    },
  });
};
